import { useEffect, useRef, useState } from "react"
import { MessageResponse } from "../api/requests/interfaces/MessageResponse";
import { useNavigate, useParams } from "react-router-dom";
import { getChannelData, getMessages, markMessageAsRead } from "../api/requests/messages";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { getUserProfile, getUserPublicProfile } from "../api/requests/profile";
import { ProfileData, ProfilePublicData } from "../api/requests/interfaces/UserProfileResponse";
import { createSocket } from "../socket";
import { Socket } from "socket.io-client";
import { WebsocketReceiveMessageModel } from "../interfaces/messages";
import { PagingParameters } from "../interfaces/paging";
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { AxiosRequestConfig } from "axios";
import api from "../api/apiInstance";
import { FileModel, FileUploadResponse } from "../api/requests/interfaces/FileModels";
import { FileSendPreview, FileUploadPreviewStatus } from "../components/chat/FileSendPreview";
import { addNotification, NotificationType } from "../store/features/notificationsSlice";
import { PrimaryButton } from "../components/buttons/PrimaryButton";
import { differenceInDays, differenceInHours, format, isAfter, isBefore, isSameDay, isToday, isYesterday } from "date-fns";
import { FileMessagePreview } from "../components/chat/FileMessagePreview";
import { ChatRentalStatusChangeWebsocketNotification, SOCKET_CONNECTION_TYPE } from "../interfaces/websockets";
import { ChatChannelData, NewChannelMessageFromSocket, RentalStatus, UserChannelElementListResponse } from "../api/requests/interfaces/Channel";
import { SecondaryButton } from "../components/buttons/SecondaryButton";
import { DisplayType } from "../store/features/displaySlice";
import ChatIcon from '@mui/icons-material/Chat';
import formatLimitedText from "../components/hooks/formatLimitedText";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { et } from "date-fns/locale";
import { ChatMessageSkeleton } from "../components/skeletons/ChatMessageSkeleton";
import Popup from "../components/common/Popup";
import { activateRental, deleteRental, leaveRetalChat } from "../api/requests/rental";
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { DeleteButton } from "../components/buttons/DeleteButton";
import useClickOutsideRef from "../components/hooks/useClickOutsideRef";
import { SpinnerContainer } from "../components/common/SpinnerContainer";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {getUnreadNotificationsCount, markNotificationsAsRead} from "../api/requests/notifications";
import {setUnseenNotificationsCount} from "../store/features/inboxSlice";
import { mainStylis } from "styled-components/dist/models/StyleSheetManager";

export interface ChatViewProps {
    data?: UserChannelElementListResponse;
    chatId: number;
    setMobileChatsView?: () => void;
    handleNewMessageInChat?: (data: NewChannelMessageFromSocket) => void;
}

export const ChatView: React.FC<ChatViewProps> = (props) => {
    const authState = useAppSelector((state) => state.auth.user);

    const [channelData, setChannelData] = useState<ChatChannelData>();

    const [messages, setMessages] = useState<MessageResponse[]>([]);
    const [myProfileData, setMyProfileData] = useState<ProfileData | undefined>();
    const [senderProfileData, setSenderProfileData] = useState<ProfilePublicData | undefined>();
    const [chatRoomSocket, setChatRoomSocket] = useState<Socket | null>(null);
    const [firstUnseenMessageId, setFirstUnseenMessageId] = useState<number | undefined>(undefined);
    const [messageText, setMessageText] = useState("");
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const [paging, setPaging] = useState<PagingParameters>({ skip: 0, take: 10 });
    const [totalMessages, setTotalMessages] = useState(0);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const initialMessagesLoaded = useRef(false);
    const initialScrollExecuted = useRef(false);
    const newMessageScroll = useRef(false);
    const isLoadingMessagesRef = useRef(false);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false); // Tracks if we need to scroll to the bottom
    const shouldMaintainScrollAfterElementsAdd = useRef(false); // Tracks if we need to scroll to the bottom
    const previousScrollHeightRef = useRef(0);
    const previousScrollTopRef = useRef(0);
    const noScrollContainer = useRef(true);
    const previousMessagesSkip = useRef(0);
    const totaleMessagesLenRef = useRef(0);
    const currrentMessagesLenRef = useRef(0);
    const initialRequestLoading = useRef(false);
    const previousResultSkipRef = useRef(0);
    const previousPaging = useRef<PagingParameters>({ skip: 0, take: 10 });
    const [uploadDocument, setUploadDocument] = useState<FileModel | undefined>(undefined);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [messageSent, setMessageSent] = useState(false);
    const firstMessageReceivedRef = useRef(false);
    const chatScrollableContainerRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useAppDispatch();

    const [sendApprovalPopup, setSendApprovalPopup] = useState<boolean>(); // Action confirmation popup
    const [deleteApprovalPopup, setDeleteApprovalPopup] = useState<boolean>(false);
    const [leaveRentalApprovalPopup, setLeaveRentalApprovalPopup] = useState<boolean>(false);
    const [showContractDownloads, setShowContractDownloads] = useState<boolean>(false);

    const [loadingDeletingRental, setLoadingDeletingRental] = useState(false);

    const chatOptionsContainerRef = useRef(null);
    const [isChatOptionsOpen, setIsChatOptionsOpen] = useState(false);
    const chatOptionsOpen = useClickOutsideRef(chatOptionsContainerRef, isChatOptionsOpen);
    const [loadingLeavingRentalChat, setLoadingLeavingRentalChat] = useState(false);

    const navigate = useNavigate();

    let lastMessage = "";

    const uploadElem = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (authState?.accessToken && props.chatId) {
            // don't create new new socket instance always in this step but rather update only auth value of socket 

            const onReceiveRoom = (msg: WebsocketReceiveMessageModel) => {
                // console.log('Received message on client from room', value);
                firstMessageReceivedRef.current = true;
                setMessages((oldState) => [...oldState, { id: msg.id, message: msg.message, userId: msg.userId, documentId: msg.documentId, 
                    chatChannelId: Number(props.chatId), sentAt: msg.sentAt, fileName: msg.fileName, seen: true
                 }])
                 props.handleNewMessageInChat && props.handleNewMessageInChat({
                    lastMessageId: msg.id,
                    lastMessageAtDate: new Date(),
                    lastMessageUserId: msg.userId,
                    lastMessageMessage: msg.message,
                    lastMessageDocumentId: msg.documentId
                 })
                // Indicate that we should scroll to the bottom
                // container!.scrollTop = container.scrollHeight;
                // setShouldScrollToBottom(true);
                newMessageScroll.current = true;
                // update received message state as "seen" or "read"
                if (authState.id != msg.userId) {
                    handleUpdateMessageRead(msg.id);
                }
            }
            const rentalStatusChanged = (d: ChatRentalStatusChangeWebsocketNotification) => {
                setChannelData((oldState) => {
                    if (oldState) {
                        return { ...oldState, rental: { ...oldState.rental, state: d.status } }
                    }
                })
            }

            console.log('INIT CHAT2 ', authState.accessToken);
            const soc = createSocket();
            soc.auth = { token: authState.accessToken };
            // socket.io.opts.query = {room: props.chatId};
            soc.io.opts.query = { type: SOCKET_CONNECTION_TYPE.ROOM, room: props.chatId };
            // socket.io.opts.forceNew = true;
            soc.connect();
            setChatRoomSocket(soc);
            soc.emit('joinRoom', "");

            soc.on('connect_error', (err: any) => { console.log("connection errors", err.data) })
            soc.on('connect_failed', err => { console.log("connection failed", err) })

            // Listen for room messages
            soc.on('roomMessage', onReceiveRoom);
            soc.on('roomStatusChange', rentalStatusChanged);
            return () => {
                soc.off('roomMessage', onReceiveRoom);
                soc.off('roomStatusChange', rentalStatusChanged);
                soc.disconnect();
            };
        }
    }, [props.chatId, authState])

    // useEffect(() => { // Scroll to bottom on initial page load
    //     if (messagesContainerRef.current && initialRequestLoading.current) {
    //         messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    //     }
    // }, [initialRequestLoading.current])

    useEffect(() => {
        return () => {
            deleteDocument();
            // call endpoint to mark all new chat messages as read that were unseen for the user after 
            if (props.chatId) {
                // console.log("CALL THIS");
                // markChatMessagesAsRead(props.chatId);
            }
        };
    }, [])

    useEffect(() => {
        console.log("CALL INIT")
        if (props.chatId && !initialRequestLoading.current) fetchInitialMessages(Number(props.chatId));
        if (!senderProfileData && props.chatId && authState) fetchChannelData(Number(props.chatId));
    }, [props.chatId])

    // Scroll to the bottom whenever messages are updated and `shouldScrollToBottom` is true
    useEffect(() => {
        currrentMessagesLenRef.current = messages.length
        /* if (shouldScrollToBottom && messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            container.scrollTop = container.scrollHeight;

            // Reset the flag after scrolling
            setShouldScrollToBottom(false);
        } */
        // if (shouldMaintainScrollAfterElementsAdd.current && messagesContainerRef.current) {
        //     const container = messagesContainerRef.current;
        //     // Save the current scroll position and total height
        //     const previousScrollHeight = previousScrollHeightRef.current;
        //     const previousScrollTop = previousScrollTopRef.current;

        //     const newScrollHeight = container.scrollHeight;
        //     const heightDifference = newScrollHeight - previousScrollHeight;

        //     // Maintain scroll position relative to the new content
        //     container.scrollTop = previousScrollTop + heightDifference;
        //     shouldMaintainScrollAfterElementsAdd.current = false;
        // }
        checkIfNoScroll();
        if (messages.length > 0 && noScrollContainer.current && !messageSent && !firstMessageReceivedRef.current && totaleMessagesLenRef.current > currrentMessagesLenRef.current) {
            fetchMessages(Number(props.chatId)) // this should load initial messages until screen is full of messages or all messages are loaded
            // should be fired only before first message is sent after opening the chat view
        }
    }, [messages]); // Dependency on `messages`

    useEffect(() => {
        // scroll user to bottom by difference amount
        if (messagesContainerRef.current && initialMessagesLoaded.current && (!initialScrollExecuted.current || newMessageScroll.current) && senderProfileData && channelData) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            initialScrollExecuted.current = true;
            newMessageScroll.current = false;
        }
        if (messages.length > 0 && props.chatId) {
            handleChannelNotificationsReadAndUpdateUnreadNotificationsCount();
        }
    }, [messages.length, initialMessagesLoaded.current, senderProfileData, channelData])

    useEffect(() => {
        if (messagesContainerRef.current && shouldMaintainScrollAfterElementsAdd.current) {
            console.log("NEW SCROLL HEIGHT ", messagesContainerRef.current?.scrollHeight, previousScrollHeightRef.current);
            console.log("SCROLL TOP ", previousScrollTopRef.current);

            const differenceInScrollContainerHeights = messagesContainerRef.current?.scrollHeight - previousScrollHeightRef.current;
            messagesContainerRef.current.scrollTop = differenceInScrollContainerHeights + previousScrollTopRef.current;
        }
    }, [messages.length, shouldMaintainScrollAfterElementsAdd.current])

    useEffect(() => {
        if (authState) {
            if (chatRoomSocket) {
                chatRoomSocket.auth = { token: authState.accessToken };
            }
            // if (!myProfileData) fetchUserProfile(authState.id);
            if (!senderProfileData && props.chatId) fetchChannelData(Number(props.chatId));
        }
    }, [authState])

    const reloadStates = () => {
        // set all states to initial value after changing chatId
        setMessages([]);
        setMyProfileData(undefined);
        setSenderProfileData(undefined);
        setChatRoomSocket(null);
        setMessageText("")
        setPaging({ ...paging, skip: 0, take: 10 });
        setTotalMessages(0);
        setIsLoadingMessages(false);
        isLoadingMessagesRef.current = false;
        setShouldScrollToBottom(false);
        shouldMaintainScrollAfterElementsAdd.current = false;
        previousScrollHeightRef.current = 0
        previousScrollTopRef.current = 0;
        noScrollContainer.current = true;
        previousMessagesSkip.current = 0;
        totaleMessagesLenRef.current = 0;
        currrentMessagesLenRef.current = 0;
        initialRequestLoading.current = false;
        previousResultSkipRef.current = 0;
        previousPaging.current = { skip: 0, take: 10 };
        setUploadDocument(undefined);
        setUploadingFile(false);
        setMessageSent(false);
        firstMessageReceivedRef.current = false;
    }

    const setInitialMessages = (newMessages: MessageResponse[]) => {
        if (messagesContainerRef.current) {
            const container = messagesContainerRef.current;

            // Save the current scroll position and total height
            const previousScrollHeight = container.scrollHeight;
            const previousScrollTop = container.scrollTop;

            // Add new messages to the top of the list
            setMessages((prevMessages) => {
                const msgArray = [...newMessages]
                msgArray.reverse()
                return [...msgArray]
            });

            initialMessagesLoaded.current = true;

            // After DOM update, adjust the scroll position
            setTimeout(() => {
                const newScrollHeight = container.scrollHeight;
                const heightDifference = newScrollHeight - previousScrollHeight;

                // Maintain scroll position relative to the new content
                // container.scrollTop = previousScrollTop + heightDifference;
                // container.scrollTop = container.scrollHeight;
            }, 0); // Delay ensures that state and DOM updates are applied
        }
    };

    const handleChannelNotificationsReadAndUpdateUnreadNotificationsCount =
        async () => {
        await markNotificationsAsRead(props.chatId);
        const countNewUnreadNotifications = await getUnreadNotificationsCount();
        dispatch(setUnseenNotificationsCount(countNewUnreadNotifications));
    }

    const handleUpdateMessageRead = async (messageId: number) => {
        await markMessageAsRead(messageId);
    }

    const prependMessages = (newMessages: MessageResponse[]) => {
        if (messagesContainerRef.current) {
            const container = messagesContainerRef.current;

            //TODO: scroll user to bottom by this amount that new messages would add such that user keeps its scroll position

            // Save the current scroll position and total height
            const previousScrollHeight = container.scrollHeight;
            const previousScrollTop = container.scrollTop;
            console.log("PREV SCROLL HEIGHT ", previousScrollHeight);

            // Add new messages to the top of the list
            setMessages((prevMessages) => {
                const msgArray = [...newMessages]
                msgArray.reverse()
                console.log("PREV MESSAGES, NEW ", prevMessages, newMessages)
                return [...msgArray, ...prevMessages]
            });

            previousScrollHeightRef.current = previousScrollHeight;
            previousScrollTopRef.current = previousScrollTop;
            shouldMaintainScrollAfterElementsAdd.current = true;

            // // After DOM update, adjust the scroll position
            // setTimeout(() => {
            //     const newScrollHeight = container.scrollHeight;
            //     const heightDifference = newScrollHeight - previousScrollHeight;

            //     // Maintain scroll position relative to the new content
            //     container.scrollTop = previousScrollTop + heightDifference;
            // }, 0); // Delay ensures that state and DOM updates are applied
        }
    };

    const fetchInitialMessages = async (channelId: number, reqPaging?: PagingParameters) => {
        console.log('here', messages)
        initialRequestLoading.current = true;
        setIsLoadingMessages(true);
        isLoadingMessagesRef.current = true;
        const messagesRes = await getMessages(channelId, 0, reqPaging ? reqPaging.take : paging.take);
        setFirstUnseenMessageId(messagesRes.firstUnseenMessageId);
        const newPaging = {
            skip: messagesRes.skip + messagesRes.data.length,
            take: messagesRes.take
        }
        setPaging(newPaging)
        previousPaging.current = newPaging;
        setIsLoadingMessages(false);
        isLoadingMessagesRef.current = false;
        setTotalMessages(messagesRes.total)
        totaleMessagesLenRef.current = messagesRes.total
        // const newMessagesList = [...messagesRes.data, ...messages]
        // setMessages([...newMessagesList])
        previousMessagesSkip.current = newPaging.skip;
        setInitialMessages(messagesRes.data);
        let mostRecentMsg = messagesRes.data[0]
        props.handleNewMessageInChat && props.handleNewMessageInChat({
            lastMessageId: mostRecentMsg.id,
            lastMessageAtDate: mostRecentMsg.sentAt,
            lastMessageUserId: mostRecentMsg.userId,
            lastMessageMessage: mostRecentMsg.message,
            lastMessageDocumentId: mostRecentMsg.documentId
        })
        // checkScrollAfterRequest(newMessagesList, messagesRes.total, newPaging);
    }

    const fetchMessages = async (channelId: number, useSkipRef?: boolean) => {
        console.log('here2', paging)
        // if ((useSkipRef && previousMessagesSkip.current <= previousResultSkipRef.current) || 
        // (!useSkipRef && paging.skip <= previousResultSkipRef.current)) {
        //     return;
        // }
        if (isLoadingMessagesRef.current) {
            return;
        }
        setIsLoadingMessages(true);
        isLoadingMessagesRef.current = true;
        const messagesRes = await getMessages(channelId, useSkipRef ? previousMessagesSkip.current : paging.skip, paging.take);
        const prevSkip = messagesRes.skip;
        const newPaging = {
            skip: messagesRes.skip + messagesRes.data.length,
            take: messagesRes.take
        }
        setIsLoadingMessages(false);
        isLoadingMessagesRef.current = false;
        setTotalMessages(messagesRes.total)
        totaleMessagesLenRef.current = messagesRes.total
        // const newMessagesList = [...messagesRes.data, ...messages]
        // setMessages([...newMessagesList])
        if (newPaging.skip <= previousMessagesSkip.current) {
            return;
        }
        previousResultSkipRef.current = prevSkip;
        previousMessagesSkip.current = newPaging.skip;
        setPaging(newPaging);
        previousPaging.current = newPaging;
        prependMessages(messagesRes.data)
        // checkScrollAfterRequest(newMessagesList, messagesRes.total, newPaging);
    }

    // const fetchUserProfile = async (profileId: number) => {
    //     const userProfile = await getUserProfile(profileId);
    //     setMyProfileData(userProfile.data);
    // }

    const fetchChannelData = async (channelId: number) => {
        const channelData = await getChannelData(channelId);
        setChannelData(channelData);
        let senderProfile;
        let userProfile;
        if (channelData.chat.providerId === authState?.id) {
            senderProfile = await getUserPublicProfile(channelData.chat.renterProfileId);
            userProfile = await getUserProfile(channelData.chat.providerProfileId);
        } else {
            senderProfile = await getUserPublicProfile(channelData.chat.providerProfileId);
            userProfile = await getUserProfile(channelData.chat.renterProfileId);
        }
        setSenderProfileData(senderProfile.data);
        setMyProfileData(userProfile.data);
    }

    // check if no scroll fill the messages of remaining screen
    const checkIfNoScroll = () => {
        const div = messagesContainerRef.current;

        if (div) {
            const noScroll = div.scrollHeight <= div.clientHeight;
            noScrollContainer.current = noScroll;
        }
    }

    const checkScroll = () => {
        const div = messagesContainerRef.current;

        if (div) {

            const isBottom =
                div.scrollHeight - div.scrollTop <= div.clientHeight + 1; // To hide "UUS SÕNUM" button when scrolled to bottom
            isBottom && setShouldScrollToBottom(false);


            const noScroll = div.scrollHeight <= div.clientHeight;
            const scrolledToTop = div.scrollTop <= 10;

            if (noScroll) {
            } else if (scrolledToTop) {
                console.log('Div is scrolled to the top.');
                if (totaleMessagesLenRef.current > currrentMessagesLenRef.current) {
                    fetchMessages(Number(props.chatId), true);
                }
            } else {
                console.log('Div has a scroll bar and is not scrolled to the top.');
            }
        }
    };

    useEffect(() => {
        // Call initially
        // checkScroll();

        // Optionally add a scroll event listener
        const div = messagesContainerRef.current;
        if (div) {
            div.addEventListener('scroll', checkScroll);
        }

        return () => {
            if (div) {
                div.removeEventListener('scroll', checkScroll);
            }
        };
    }, []);

    const sendMessage = () => {
        if (!messageText.length && !uploadDocument) {
            return
        }
        chatRoomSocket!.emit('sendToRoom', {
            message: (messageText.length && !uploadDocument) ? messageText : undefined, documentId: uploadDocument ? uploadDocument?.id : undefined,
            fileName: uploadDocument ? uploadDocument?.name : undefined
        });
        setUploadDocument(undefined);
        setMessageText("");
        setMessageSent(true);
    };

    const handleUploadAreaClick = () => {
        if (uploadElem.current) {
            uploadElem.current.click();
        }
    }

    const handleStartFileUpload = async (files: FileList | null) => {
        if (files === null || files?.item(0) === null) {
            return;
        }
        setUploadDocument({
            id: undefined,
            key: undefined,
            name: files[0].name
        })
        setUploadingFile(true);
        const config: AxiosRequestConfig = {
            onUploadProgress: progressEvent => {
                var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);
                console.log("FILE PROGRESS, ", percentCompleted)
            },
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }

        var data = new FormData();
        data.append('file', files[0]);

        try {
            const res = await api.post<FileUploadResponse>(process.env.REACT_APP_RENTIC_API_URL! + "/files/contract_upload/" + props.chatId, data, config);
            setUploadingFile(false);
            setUploadDocument({ ...uploadDocument, key: res.data.key, id: res.data.id, name: files[0].name })
        } catch (e) {
            setUploadDocument(undefined);
            setUploadingFile(false);
            dispatch(addNotification({ message: "Dokumendi üleslaadimine ebaõnnestus", type: NotificationType.ERROR }))
        }
    }


    const deleteDocument = async () => {
        try {
            if (uploadDocument) {
                const res = await api.delete<number>(process.env.REACT_APP_RENTIC_API_URL! + "/files/delete_file/" + uploadDocument?.id + "/" + props.chatId);
                setUploadDocument(undefined);
            }
        }
        catch (e) {
            dispatch(addNotification({ message: "Dokumendi üleslaadimine ebaõnnestus", type: NotificationType.ERROR }))
        }
    }

    const checkIfScrolledToBottom = (ref: React.RefObject<HTMLDivElement>) => {
        const container = ref.current;
        if (container) {
            const isBottom =
                container.scrollHeight - container.scrollTop <= container.clientHeight + 1; // Allow small tolerance
            return isBottom;
        }
        return false;
    };

    const handleRentalApproval = async () => {
        setSendApprovalPopup(false);
        if (channelData) {
            setChannelData({ ...channelData, rental: { ...channelData.rental, state: RentalStatus.IN_RENT } });
            await activateRental(channelData.rental.id);
        }
        // TODO: rental approved
        console.log(props.data?.chat.buyerApproval);
    }

    // only chat owner so seller needs to be able to perform this action
    const handleDeleteRental = async () => {
        setLoadingDeletingRental(true);
        if (channelData) {
            try {
                await deleteRental(channelData.rental.id);
                dispatch(addNotification({ message: "Renditaotlus kustutatud edukalt", type: NotificationType.SUCCESS }))
                setLoadingDeletingRental(false);
                setDeleteApprovalPopup(false);
                // reload chats page as chat was deleted
                window.location.reload();
            } catch (e) {
                setLoadingDeletingRental(false);
                setDeleteApprovalPopup(false);
                dispatch(addNotification({ message: "Renditaotluse kustutamine ebaõnnestus", type: NotificationType.ERROR }))
            }
        }
    }

    const handleRentalLeave = async () => {
        setLoadingLeavingRentalChat(true);
        try {
            if (channelData) {
                await leaveRetalChat(channelData.rental.id);
                dispatch(addNotification({ message: "Vestlusest edukalt lahkutud", type: NotificationType.SUCCESS }));
                window.location.reload();
            }
            setLoadingLeavingRentalChat(false);
        } catch (e) {
            setLoadingLeavingRentalChat(false);
            dispatch(addNotification({ message: "Vestlusest lahkumine ebaõnnestus", type: NotificationType.ERROR }));
        }
    }

    const handleRentalCancel = () => {
        setSendApprovalPopup(false);
        alert("cancel rental");
        // TODO: rental canceled
        console.log(props.data?.chat.buyerApproval);
    }

    const getFormattedTotalRentalPrice = (amount: number, startDate: Date, endDate: Date) => {
        const daysDiff = differenceInDays(endDate, startDate);
        const dayString = daysDiff > 1 ? "päeva" : daysDiff == 1 ? "päeva" : "";
        const newEndDate = startDate;
        newEndDate.setMonth(endDate.getMonth())
        newEndDate.setFullYear(endDate.getFullYear())
        newEndDate.setDate(endDate.getDate())
        const hoursDiff = differenceInHours(endDate, newEndDate);
        let hoursString = hoursDiff > 1 ? "tundi" : hoursDiff == 1 ? "tund" : "";
        let lengthString = "";
        if (dayString.length) {
            lengthString += daysDiff + " " + dayString;
        }
        if (hoursString.length) {
            if (dayString.length) {
                lengthString += " ";
            }
            lengthString += hoursDiff + " " + hoursString;
        }

        return amount.toString() + "€ (" + lengthString + ")";
    }

    const contractDownloadsPopup = <div className="bg-white rounded-lg shadow-lg p-6 w-[400px]">
        <p className="text-gray-800 text-lg font-semibold mb-4">Lepingu kasutamine</p>
      <p className="text-gray-800 text-sm mb-4">Tegemist on näidislepingu põhjadega ja klient viib vajadusel ise omapoolsed korrektuurid sisse ning meie kui vahendajad ei vastuta sisu eest! Kes soovib, saab üldse omapoolset lepingut kasutada!</p>
      <p className="text-gray-800 text-lg font-semibold mb-4">Lae alla vastav leping</p>
    <div className="flex flex-row gap-2 mb-5">
        <button
        className="bg-primary hover:bg-primary_darker text-white px-6 py-1 rounded-xl transition text-sm"
        onClick={() => {window.open("https://rentic-storage.s3.eu-north-1.amazonaws.com/rental_base_contracts/Soiduki+rendileping.docx", '_blank');}}
      >
        Sõiduk
      </button>
        <button
        className="bg-primary hover:bg-primary_darker text-white px-6 py-1 rounded-xl transition text-sm"
        onClick={() => {window.open("https://rentic-storage.s3.eu-north-1.amazonaws.com/rental_base_contracts/Seadme+rendileping.docx", '_blank');}}
      >
        Seade
      </button>
        <button
        className="bg-primary hover:bg-primary_darker text-white px-6 py-1 rounded-xl transition text-sm"
        onClick={() => {window.open("https://rentic-storage.s3.eu-north-1.amazonaws.com/rental_base_contracts/ELURUUMI+ÜÜRILEPING.docx", '_blank');}}
      >
        Kinnisvara
      </button>
    </div>
    <div className="flex justify-end gap-3">
      
      <button
        className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-xl transition"
        onClick={() => setShowContractDownloads(false)}
      >
        Sulge
      </button>
    </div></div>

    return (
        <SpinnerContainer show={loadingLeavingRentalChat} fullWidth={true}>
            <div className="flex flex-col w-full h-full gap-2">
                <div className="relative flex flex-col w-full flex-auto py-2 lg:px-2 rounded-xl shadow-main h-full">
                    <div className="flex flex-row gap-2 w-full mb-2">
                        {window.innerWidth < 1024 &&
                            <div onClick={() => { props.setMobileChatsView && props.setMobileChatsView() }} className="flex justify-center items-center bg-gray-200 shadow-main rounded-xl h-full w-[60px] cursor-pointer">
                                <ChatIcon style={{ color: "#65676b" }} />
                            </div>}
                        {(senderProfileData && channelData) &&
                            <div className="flex flex-row justify-between box-border w-full p-1 pb-2 lg:p-3 rounded-xl shadow-main">

                                <div className="flex flex-row gap-3 pl-2">
                                    {window.innerWidth > 1024 &&
                                        <div className="pt-2">
                                            <img className="w-[50px] h-[50px] rounded-full object-cover" src={senderProfileData.profilePicUrl} alt="" />
                                        </div>
                                    }
                                    <div className="flex flex-col">
                                        <h3 className="">{senderProfileData.name} {senderProfileData.verified && <VerifiedUserIcon style={{ width: "18px", height: "18px" }} className="text-primary" />}</h3>
                                        <div className="flex flex-row gap-1 items-center">
                                            <PlaceOutlinedIcon style={{ width: "16px", height: "16px" }} />
                                            <h3 className="font-light text-[12px] lg:text-sm">{channelData.rental.product.location}</h3>
                                        </div>

                                        <h4 className=" font-semibold text-[16px]">{formatLimitedText(18, channelData.rental.product.title)}</h4>
                                        <div className="flex flex-row text-sm">Staatus: {channelData.rental.state === RentalStatus.MESSAGE_SENT ?
                                            <div className="font-semibold text-yellow-500 pl-1">Renditaotlus</div> :
                                            channelData.rental.state === RentalStatus.IN_RENT ?
                                                <div className="font-semibold text-green-500 pl-1">Kinnitatud</div>
                                                :
                                                <div className="font-semibold">Arhiveeritud</div>
                                        }
                                        </div>
                                    </div>

                                    {/* <div className="flex flex-col justify-end h-full pb-1">
                                <div className="flex flex-row">Staatus: {channelData.rental.state === RentalStatus.MESSAGE_SENT ? 
                                    <div className="font-bold text-yellow-500 pl-1">Sõnum saadetud</div> :
                                    channelData.rental.state === RentalStatus.IN_RENT ?
                                    <div className="font-bold text-green-500 pl-1">Rendis</div>
                                    :
                                    <div className="font-bold">Arhiveeritud</div>
                                    }
                                </div>
                            </div> */}
                                </div>

                                <div className="flex flex-col text-right pr-2 items-end justify-between">
                                    <div className="flex flex-col">
                                        <h3 className="">{channelData.rental.product.dayPrice}€/päev </h3>
                                        <h4 className=" font-light text-[14px] lg:text-[16px]">Kokku: {getFormattedTotalRentalPrice(channelData.rental.amount, new Date(channelData.rental.startDate), new Date(channelData.rental.endDate))}</h4>
                                    </div>
                                    <div className="rounded-xl bg-primary text-white px-1 lg:px-2 text-[13px] lg:text-sm mt-2 w-fit">
                                        {format(channelData.rental.startDate, 'dd. MMM HH:mm', { locale: et })} – {format(channelData.rental.endDate, 'dd. MMM HH:mm', { locale: et })}
                                    </div>
                                </div>

                                {/* <div className="flex flex-row justify-between h-[22px] lg:h-auto">
                            <div></div>
                            <div>
                                <h3 className="text-white">{props.data?.chat.renterProfileName}</h3>
                            </div>
                            <div className="flex flex-row items-center gap-2">
                                {window.innerWidth > 1024 &&
                                    <div className="flex flex-row gap-2 items-center">
                                        <h4 className="text-white">{props.data?.rental.product.title}</h4>
                                        <div className="font-semibold text-xl text-white">•</div>
                                    </div>
                                }

                                <h3 className="text-white font-normal text-sm lg:text-[15px]">12/13 - 12/02/2025</h3>
                                <div className="font-semibold text-xl text-white">•</div>
                                <div className="flex flex-row items-baseline">
                                    <h3 className="text-white font-semibold text-sm lg:text-[20px]">15€</h3>
                                    <h3 className="text-white font-normal text-sm lg:text-[15px]">/päev</h3>
                                </div>
                            </div>
                        </div>
                        {window.innerWidth <= 1024 &&
                            <div>
                                <h4 className="text-white font-normal text-sm">{props.data?.rental.product.title}</h4>
                            </div>
                        } */}
                            </div>
                        }
                    </div>
                    <div className="relative min-h-[220px] flex-grow">
                        {messagesContainerRef.current && shouldScrollToBottom && !checkIfScrolledToBottom(messagesContainerRef) &&
                            <div onClick={() => {
                                if (messagesContainerRef.current) {
                                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                                    setShouldScrollToBottom(false);
                                }
                            }} className="absolute flex flex-row items-center justify-center px-2 cursor-pointer h-[40px] bottom-2 bg-white rounded-full right-[50%] translate-x-[50%] border-primary border-[1px]">
                                <ExpandMoreIcon className="text-primary w-[35px]" />
                                <div className="text-primary text-[10px]">UUS SÕNUM</div>

                            </div>
                        }

                        <div className="overflow-y-auto h-full flex-auto pr-2" ref={messagesContainerRef}   >
                            <div className="flex min-h-full flex-col justify-end w-full">
                                {isLoadingMessages &&
                                    // <div>loading</div>
                                    // }
                                    Array.from({ length: 10 }, (_, index) => (
                                        <ChatMessageSkeleton key={index} width={0} alignment={Math.random() < 0.5 ? "left" : "right"} />
                                    ))}

                                {messages.map((msg, inx) => {

                                    // Track the previous message date
                                    const currentDate = new Date(msg.sentAt);
                                    const previousDate = inx > 0 ? new Date(messages[inx - 1].sentAt) : null;

                                    let previousMessageSeen = inx > 0 ? (messages[inx - 1].seen && messages[inx - 1].userId != authState?.id) : null;
                                    if (inx === 0) {
                                        previousMessageSeen = true;
                                    }
                                    let currentMessageStartOfNewMessages = !msg.seen && (previousMessageSeen !== null) && (previousMessageSeen != msg.seen) && (msg.userId !== authState?.id)
                                    // console.log("msg seen ", msg.seen)

                                    const isNewDay = !previousDate || !isSameDay(currentDate, previousDate);

                                    return (
                                        <>
                                            {isNewDay && (
                                                <div className="flex flex-row gap-2 items-center justify-end text-center text-gray-500 font-light text-md my-2" key={inx}>
                                                    <div className=" bg-transparent w-full"></div>
                                                    <div className="text-nowrap">

                                                        {isYesterday(currentDate)
                                                            ? "Eile"
                                                            : isToday(currentDate) ? "Täna" : format(currentDate, "MMMM dd, yyyy", { locale: et })}</div>
                                                    <div className="h-[1px] bg-primary opacity-30 w-full"></div>

                                                </div>
                                            )}

                                            {(msg.id === firstUnseenMessageId) && (
                                                <div className="flex flex-row gap-2 items-center justify-end text-center text-red-500 font-light text-md my-2" key={"new_" + inx.toString()}>
                                                    <div className=" bg-transparent w-full"></div>
                                                    <div className="text-nowrap">
                                                        Uus
                                                    </div>
                                                    <div className="h-[1px] bg-red-500 opacity-30 w-full"></div>

                                                </div>
                                            )}
                                            {/*do similar stuff as displaying the day bar by showing the "new bar
                                            if new message is not seen and previous message is seen"*/}

                                            {msg.documentId ?
                                                <div key={"message_" + inx.toString()} className={`flex w-full py-1 ${msg.userId === authState?.id ? "justify-end" : "justify-start"}`}>
                                                    <FileMessagePreview key={msg.id} fileId={msg.documentId} fileName={msg.fileName!} sentAt={msg.sentAt} isSender={msg.userId !== authState?.id} chatId={Number(props.chatId)} />
                                                </div>
                                                :

                                                <div key={"message_" + inx.toString()} className={`flex flex-row w-full gap-2 py-1 ${msg.userId === authState?.id ? "flex-row-reverse" : "justify-start"}`}>
                                                    <div className="w-[30px] h-[30px]">
                                                        <img className="rounded-full h-full w-full object-cover" src={msg.userId === authState?.id ? myProfileData?.profilePicUrl : senderProfileData?.profilePicUrl} alt="" />
                                                    </div>
                                                    <div className={`flex flex-col w-fit max-w-[65%] lg:max-w-[50%] rounded-xl ${msg.userId === authState?.id ? "bg-chat" : "bg-primary_light"} p-2 whitespace-pre-wrap`}>
                                                        <div className={`${msg.userId === authState?.id ? "text-right" : "text-left"}`}>{msg.message}</div>
                                                        <div className={`${msg.userId === authState?.id ? "text-right" : "text-left"} text-xs`}>{format(msg.sentAt, "HH:mm").toString()}</div>
                                                    </div>
                                                </div>}

                                        </>)

                                })
                                }
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-row mt-2">
                        <div className="flex flex-row gap-2 border-solid border-2 border-gray-400 p-2 flex-1 rounded-xl">
                            <div className="flex-1">
                                {
                                    uploadDocument ?
                                        <FileSendPreview id={1} status={uploadingFile ?
                                            FileUploadPreviewStatus.LOADING : FileUploadPreviewStatus.UPLOADED}
                                                         name={uploadDocument.name}
                                            onRemoveClick={() => deleteDocument()} />
                                        :
                                        <textarea placeholder="Kirjuta" onChange={(e) => setMessageText(e.target.value)} value={messageText} className="h-[50px] resize-none pb-0 border-none border-black w-full focus:outline-none focus:ring-0 focus:border-none focus:select-none" />
                                }
                                {/*<div className="text-[10px]">Rentic ei vastuta teise kasutaja saadetud faili sisu ohutuse eest</div>*/}
                            </div>
                            <div className="flex-none cursor-pointer" onClick={() => handleUploadAreaClick()}>
                                <AttachFileOutlinedIcon style={{ width: "30px", height: "30px" }} className="rotate-45" />
                                <input type="file" id="upload_doc" name="filename"
                                       className='hidden' ref={uploadElem}
                                       accept=".pdf, .asice, .bdoc, .doc, .docx"
                                       onChange={(e) => {
                                           handleStartFileUpload(e.target.files); e.target.files = null; }} />
                            </div>
                        </div>
                        <div className="w-[100px] px-2">
                            <PrimaryButton title="Saada" textSize="m" onClick={() => sendMessage()} />
                        </div>
                    </div>
                </div>
                <div className="flex flex-row justify-between">
                    {/* <div className="g:w-[120px] text-sm">
                    <SecondaryButton title="Lepingu mall" widthFit={DisplayType.MOBILE ? true : false} transparent={true} textSize="m" onClick={() => { }} />
                </div>
                <div className="lg:w-[170px] text-sm">
                    <SecondaryButton title="Tingimuste mall" widthFit={DisplayType.MOBILE ? true : false} transparent={true} textSize="m" onClick={() => { }} />
                </div> */}
                    <div className="flex items-start flex-row">
                        {
                            (authState?.id === channelData?.chat.providerId && channelData?.rental.state === RentalStatus.MESSAGE_SENT) &&
                            <div className="lg:w-[180px] text-sm ml-2">
                                <PrimaryButton title="Kinnita renditaotlus" widthFit={DisplayType.MOBILE ? true : false} textSize="m" onClick={() => { setSendApprovalPopup(true) }} />
                            </div>
                        }
                        {
                            (authState?.id === channelData?.chat.providerId && (channelData?.rental.state === RentalStatus.MESSAGE_SENT || (channelData?.rental.state === RentalStatus.IN_RENT && (isBefore(new Date(), channelData?.rental.startDate) || isAfter(new Date(), channelData?.rental.endDate))))) &&
                            <div className="lg:w-[180px] text-sm ml-2">
                                <DeleteButton outlined title="Loobu renditaotlusest" textSize="m" onClick={() => { setDeleteApprovalPopup(true) }} />
                            </div>
                        }
                        <div className="lg:w-[140px]">
                            {
                                <>
                                    <SecondaryButton title="Lepingud" outlined={false} icon={<FileDownloadIcon></FileDownloadIcon>} widthFit={DisplayType.MOBILE ? true : false} textSize="m" onClick={() => {setShowContractDownloads(!showContractDownloads)}} />
                                    {

                                    }
                                </>
                            }
                        </div>
                    </div>

                    <div className="w-full flex flex-row justify-end text-sm ml-2 relative" ref={chatOptionsContainerRef}>

                        <div className="lg:w-[180px]">
                            {
                                channelData && isBefore(channelData?.rental.endDate, new Date()) &&
                                <>
                                    <PrimaryButton title="Muud valikud" widthFit={DisplayType.MOBILE ? true : false} textSize="m" onClick={() => { setIsChatOptionsOpen(true) }} />
                                    {
                                        chatOptionsOpen &&
                                        <div className="absolute w-[120px] top-[-110%] right-0 shadow-md rounded-lg bg-gray-200 mb-[50px]">
                                            {
                                                channelData && isBefore(channelData?.rental.endDate, new Date()) &&
                                                <div className="text-red-500 hover:bg-gray-300 p-2 rounded-lg cursor-pointer" onClick={() => setLeaveRentalApprovalPopup(true)}>Lahku vestlusest</div>
                                            }
                                        </div>
                                    }
                                </>
                            }
                        </div>


                    </div>
                </div>
                {sendApprovalPopup &&
                    <Popup message="Oled kindel, et kinnitada renditaotlus?" subText={"Teine kasutaja saab teavituse rendi alustamiseks ning valitud kuupäevad reserveeritakse antud rendile."} onConfirm={() => { handleRentalApproval() }} onCancel={() => { setSendApprovalPopup(false) }} />
                }
                {deleteApprovalPopup &&
                    <Popup message="Oled kindel, et loobuda renditaotlusest?" subText={"Renditaotlusest loobumisest kustutatakse jäädavalt ka kogu vestlus ja dokumendid."} onConfirm={() => { handleDeleteRental() }} onCancel={() => { setDeleteApprovalPopup(false) }} loading={loadingDeletingRental} />
                }
                {leaveRentalApprovalPopup &&
                    <Popup message="Oled kindel, et soovid vestlusest lahkuda?" subText={"Rendivestlusest lahkudes kaob ka antud rendi info teie rentide vaatest."} onConfirm={() => { handleRentalLeave() }} onCancel={() => { setLeaveRentalApprovalPopup(false) }} loading={loadingLeavingRentalChat} />
                }
                {showContractDownloads &&
                    <Popup customContent={contractDownloadsPopup} />
                }


            </div>
        </SpinnerContainer>
    )
}

function updateChatSellerApproval(id: number | undefined, arg1: boolean) {
    throw new Error("Function not implemented.");
}
